import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { isNull } from 'lodash';
import { saveAs } from 'file-saver';
import lzSCommonsLastErrorMessageAtom from '@lz-atoms/Commons/lastErrorMessage';
import lzSCommonsMediaBrowserDataAtom from '@lz-atoms/Commons/mediaBrowserData';
import lzSAuthUserAtom from '@lz-atoms/Auth/user';
import LzSAppBasics from '@lz-app/AppBasics';
import { lzFNotificationsShowError, lzFNotificationsShowSuccess } from '@lz/components/commons/Notifications';
import { lzFKebabToSnakeCase } from './String';
import LzMOption from '@lz/models/commons/Option';
import { LzTNavItems } from '@lz-layout/Navigation/types';
import { LzTMediaStateDataParams, LzTOpenObject } from '@lz/typings/types';
import LzMUser from '@lz/models/commons/User';

export const lzFGetFormattedAmount = (value: number, allowDecimals: boolean): string => {
  const input = allowDecimals ? value : Math.round(value);
  return new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR' })
    .format(input)
    .replace('.00', '');
};

export const lzFGetHashFromObjectArray = (objArr: LzTOpenObject[], idField?: string, nameField?: string): LzTOpenObject => {
  const theIdField = idField || 'id';
  const theNameField = nameField || 'name';
  const out: LzTOpenObject = {};

  for (const obj of objArr) {
    out[obj[theIdField]] = obj[theNameField];
  }

  return out;
};

export const lzFGetOptionsFromHashArray = (hash: any): LzMOption[] => {
  const out: LzMOption[] = [];

  for (const key in hash) {
    out.push(new LzMOption({ value: key, text: hash[key] }));
  }

  return out;
};

export const lzFLogInfo = (...args: any[]): void => {
  if (LzSAppBasics.debug_mode_enabled) {
    console.log(...args);
  }
};

export const lzFDownloadBlobFile = (blob: string | Blob, filename?: string): void => {
  saveAs(blob, filename);
};

export const lzFValueTagsDisplay = (val: string): string => {
  let out = '<small><b>{site-base-url}</b>';

  if (val) {
    out += ', <b>{' + val.split(',').join('}</b>, <b>{') + '}</b>';
  }

  out +=
    '<br/>use above place holders where you want to display respective data in subject and body</small>';

  return out;
};

export const lzFShowErrorMessage = (setCommonsLastErrorMessageState: any, msg: string): void => {
  setCommonsLastErrorMessageState(msg);
  lzFNotificationsShowError(msg);
};

export const useLzFShowErrorMessage = () => {
  const setCommonsLastErrorMessageState = useSetAtom(lzSCommonsLastErrorMessageAtom);

  return (msg: string) =>
    lzFShowErrorMessage(setCommonsLastErrorMessageState, msg);
};

export const lzFShowSuccessMessage = (msg: string): void => {
  lzFNotificationsShowSuccess(msg);
};

export const lzFSortByPosition = (objOfObjs: any, sortKey: string): any => {
  const order: any = [],
    res: any = {};

  Object.keys(objOfObjs).forEach((key) => {
    return (order[objOfObjs[key][sortKey] - 1] = key);
  });

  order.forEach((key: string | number) => {
    res[key] = objOfObjs[key];
  });

  return res;
};

export const lzFSortArrayOfObjectsByPosition = (inputArr: any[], sortKey: string): any[] => {
  return inputArr.slice().sort((a, b) => a[sortKey] - b[sortKey]);
};

/**
 * Position an object's element to a place specified by a number.
 *
 * @param obj The input object to process.
 * @param key The element to move to a position.
 * @param pos The position where to move the element (0 based).
 *
 * @return The updated object.
 */
export const lzFPositionObjectElementTo = (obj: any, key: string, pos: number) => {
  const out: LzTOpenObject = {};

  let runningPos = 0;
  let elementInserted = false;
  for (const runningKey in obj) {
    // If this is the element then skip it because we are positioning it manually (unless surprisingly this is the position we want it on)
    if (runningKey === key && runningPos !== pos) {
      continue;
    }

    // If this is the position where the element should be then put it there
    if (runningPos === pos) {
      out[key] = obj[key];
      elementInserted = true;
      runningPos++;
    }

    // Put the running element in the output object
    out[runningKey] = obj[runningKey];
    runningPos++;
  }

  // If by chance the element was not inserted (could be because `pos` is higher than available positions)
  if (!elementInserted) {
    out[key] = obj[key];
  }

  return out;
};

export const lzFBytesToSize = (bytes: number): string => {
  var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)) + '');
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

export const lzFShowMediaBrowser = (
  setCommonsMediaBrowserDataState: any,
  params: LzTMediaStateDataParams,
  successCallback?: CallableFunction,
  failureCallback?: CallableFunction
): void => {
  setCommonsMediaBrowserDataState({
    params: params,
    successCallback:
      typeof successCallback === 'function'
        ? successCallback
        : (media: any) => console.log('MEDIA-SELECTED', media),
    failureCallback:
      typeof failureCallback === 'function'
        ? failureCallback
        : (msg: string) => console.error('FAILED-UPLOAD', msg),
  });
};

export const useLzFShowMediaBrowser = () => {
  const setCommonsMediaBrowserDataState = useSetAtom(lzSCommonsMediaBrowserDataAtom);

  return (
    params: LzTMediaStateDataParams,
    successCallback?: CallableFunction,
    failureCallback?: CallableFunction
  ) =>
    lzFShowMediaBrowser(
      setCommonsMediaBrowserDataState,
      params,
      successCallback,
      failureCallback
    );
};

export const lzFHideMediaBrowser = (setCommonsMediaBrowserDataState: any): void => {
  setCommonsMediaBrowserDataState(null);
};

export const useLzFHideMediaBrowser = () => {
  const setCommonsMediaBrowserDataState = useSetAtom(lzSCommonsMediaBrowserDataAtom);

  return () => lzFHideMediaBrowser(setCommonsMediaBrowserDataState);
};

export const lzFGetCurrentRoutePathSegments = (): Array<string> => {
  // TODO: Handle the case of a different type of routing then Hash Routing
  return window.location.hash
    .split('/')
    .filter((x) => x)
    .slice(1);
};

export const lzFGetCurrentRoute = (): string => {
  return '/' + lzFGetCurrentRoutePathSegments().join('/');
};

export const lzFGetCurrentRouteEquivOfKeyBreadcrumb = (): string => {
  const routeSegments = lzFGetCurrentRoutePathSegments();
  const keySegments: Array<string> = [];

  for (const dex in routeSegments) {
    keySegments.push(lzFKebabToSnakeCase(routeSegments[dex]));
  }

  return keySegments.join('.');
};

/**
 * Helper hook to load a script for a component.
 *
 * @param url Location of the script to be loaded
 */
export const useLzFJavaScript = (url: string): void => {
  useEffect(() => {
    const script = document.createElement('script');

    script.src = url;
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [url]);
};

export const useLzFUrlQueryParams = () => {
  const location = useLocation();

  return () => {
    const out: LzTOpenObject = {};
    const qs = new URLSearchParams(location.search);
    qs.forEach((val, key) => (out[key] = val));
    return out;
  };
};

/**
 * Get sub-links of a nav item based on the logged in user.
 *
 * @param navItems
 * @param parentKey
 *
 * @returns array
 */
export const lzFGetNavSubLinks = (navItems: LzTNavItems, parentKey: string): LzTNavItems => {
  for (const item of navItems) {
    if (item.key === parentKey) {
      if (!item.children || item.children.length < 1) {
        return [];
      }

      const out: LzTNavItems = [];
      for (const subitem of item.children) {
        out.push(subitem);
      }

      return out;
    }
  }

  return [];
};

export const lzFDivide = (num: number, denom: number): number => {
  if (denom <= 0) {
    return num;
  }
  return num / denom;
};

export const useLzFCheckRights = () => {
  const authUserState = useAtomValue(lzSAuthUserAtom);

  if (isNull(authUserState)) {
    return (_: string): boolean => {
      console.error('User state not loaded yet!');
      return false;
    };
  }

  const user = new LzMUser(authUserState);

  return (rightsPath: string): boolean => {
    return user.checkRights(rightsPath);
  };
};

export const lzFGetFunctionName = () => {
  // TODO: Find a way to get this, currently no way
  return 'n/a';
};
